/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// ตรวจภาพโดยไม่สร้างงานใหม่; beforeSrc ต้องเป็นรูปต้นทางที่บันทึกกับงานนั้นเท่านั้น
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { downloadImage } from 'layouts/_entrio/imageDownload';

function ReviewImage({ src, label, zoom, onReady }) {
  const [size, setSize] = useState(null);
  const [failed, setFailed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const zoomed = Boolean(zoom && size);
  // ตอนซูม กดเมาส์ซ้ายค้างแล้วลากเพื่อเลื่อนดูส่วนอื่นของภาพ
  const startDrag = (e) => {
    if (!zoomed || e.button !== 0) return;
    const box = e.currentTarget;
    drag.current = { x: e.clientX, y: e.clientY, left: box.scrollLeft, top: box.scrollTop };
    box.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const moveDrag = (e) => {
    if (!drag.current) return;
    const box = e.currentTarget;
    box.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
    box.scrollTop = drag.current.top - (e.clientY - drag.current.y);
  };
  const endDrag = () => {
    drag.current = null;
    setDragging(false);
  };
  return (
    <MDBox minWidth={0}>
      <MDTypography variant='caption' fontWeight='bold'>
        {label}
        {size ? ` · ${size.w} × ${size.h} พิกเซล` : ''}
      </MDTypography>
      <MDBox
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          overflow: 'auto',
          height: '52vh',
          bgcolor: '#f3f4f6',
          borderRadius: 1,
          cursor: zoomed ? (dragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
        }}
      >
        {failed ? (
          <MDTypography variant='body2' color='error' p={2}>
            โหลดภาพไม่ได้ ปิดแล้วเปิดใหม่ (ยังไม่ควรเก็บ)
          </MDTypography>
        ) : (
          <img
            src={src}
            alt={label}
            draggable={false}
            onLoad={(e) => {
              setSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
              onReady();
            }}
            onError={() => setFailed(true)}
            style={
              zoomed
                ? {
                    width: size.w * zoom,
                    height: size.h * zoom,
                    maxWidth: 'none',
                    display: 'block',
                  }
                : { width: '100%', height: '100%', objectFit: 'contain', display: 'block' }
            }
          />
        )}
      </MDBox>
    </MDBox>
  );
}
ReviewImage.propTypes = {
  src: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  zoom: PropTypes.number.isRequired,
  onReady: PropTypes.func.isRequired,
};

function ReviewBody({ item, busy, onApprove }) {
  const [zoom, setZoom] = useState(0);
  const [ready, setReady] = useState(false);
  return (
    <>
      <MDBox display='flex' flexWrap='wrap' gap={1} alignItems='center' mb={1}>
        {[
          { value: 0, label: 'พอดีจอ' },
          { value: 1, label: '100%' },
          { value: 2, label: '200%' },
        ].map((z) => (
          <MDButton
            key={z.value}
            size='small'
            color='info'
            variant={zoom === z.value ? 'contained' : 'outlined'}
            aria-pressed={zoom === z.value}
            onClick={() => setZoom(z.value)}
          >
            {z.label}
          </MDButton>
        ))}
      </MDBox>
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: item.beforeSrc ? '1fr 1fr' : '1fr' },
          gap: 2,
        }}
      >
        {item.beforeSrc && (
          <ReviewImage
            key={`before-${item.beforeSrc}`}
            src={item.beforeSrc}
            label='ก่อน'
            zoom={zoom}
            onReady={() => {}}
          />
        )}
        <ReviewImage
          key={item.src}
          src={item.src}
          label='หลัง'
          zoom={zoom}
          onReady={() => setReady(true)}
        />
      </MDBox>
      {/* ไม่มีข้อความถาวรใต้ภาพ — คำเตือนตรวจฉลากอยู่ใน title ของปุ่ม · ใช้คำว่า "เก็บ" ทุกหน้า (18 ต.ค. 2569) */}
      <MDBox display='flex' justifyContent='flex-end' alignItems='center' gap={1} mt={1.5}>
        {item.approved ? (
          <MDTypography variant='button' color='success'>
            เก็บแล้ว
          </MDTypography>
        ) : item.canApprove ? (
          <MDButton
            color='info'
            variant='gradient'
            disabled={busy || !ready}
            title='ตรวจชื่อสินค้า ฉลาก ราคา ให้ถูกก่อนเก็บ'
            onClick={() => onApprove(item.row)}
          >
            {busy ? 'กำลังเก็บ…' : item.approveLabel || 'เก็บเข้าแกลเลอรี'}
          </MDButton>
        ) : null}
      </MDBox>
    </>
  );
}
const itemType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  fileName: PropTypes.string, // ชื่อไฟล์ตอนดาวน์โหลด (ไม่ใส่นามสกุล) · ไม่มี = ใช้ title
  beforeSrc: PropTypes.string,
  approved: PropTypes.bool,
  canApprove: PropTypes.bool,
  approveLabel: PropTypes.string,
  row: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
});
ReviewBody.propTypes = {
  item: itemType.isRequired,
  busy: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
};

export default function ImageReview({ items, selectedId, onChange, onClose, onApprove, busy }) {
  /* ปุ่มดาวน์โหลดบอกสถานะเอง (รีเช็ค 26 ต.ค.: เดิมล้มเงียบ และกดซ้ำโหลดไฟล์ใหญ่ซ้ำ) */
  const [dl, setDl] = useState('');
  const dlTimer = useRef(null);
  const download = (it) => {
    if (dl === 'busy') return;
    clearTimeout(dlTimer.current);
    setDl('busy');
    downloadImage(it.src, it.fileName || it.title)
      .then(() => setDl(''))
      .catch(() => {
        setDl('fail');
        dlTimer.current = setTimeout(() => setDl(''), 4000);
      });
  };
  const index = items.findIndex((row) => String(row.id) === String(selectedId));
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  /* ปุ่มที่กดถูกปิดเมื่อถึงสุดทาง → โฟกัสไปปุ่มอีกฝั่ง ไม่หลุดไปที่กล่อง (18 ต.ค. 2569) */
  const step = (delta) => {
    onChange(items[index + delta].id);
    requestAnimationFrame(() => {
      const me = delta < 0 ? prevRef.current : nextRef.current;
      const other = delta < 0 ? nextRef.current : prevRef.current;
      if (me && me.disabled && other && !other.disabled) other.focus();
    });
  };
  const item = items[index];
  /* สถานะปุ่มดาวน์โหลดเป็นของภาพที่ดูอยู่ — เลื่อนไปภาพอื่นล้างทิ้ง */
  const itemKey = item ? item.id : '';
  useEffect(() => {
    clearTimeout(dlTimer.current);
    setDl('');
  }, [itemKey]);
  const open = selectedId !== null;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth='lg'
      aria-labelledby='image-review-title'
    >
      <DialogTitle id='image-review-title'>
        ตรวจภาพ · {item ? item.title : 'รายการนี้เก็บแล้ว'}
      </DialogTitle>
      <DialogContent dividers>
        {item ? (
          <ReviewBody
            key={`${item.id}-${item.src}`}
            item={item}
            busy={busy}
            onApprove={onApprove}
          />
        ) : (
          <MDTypography variant='body2'>
            รายการนี้เก็บแล้วหรือไม่อยู่ในรายการแล้ว ปิดหน้านี้เพื่อดูรายการล่าสุด
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <MDBox display='flex' gap={1} alignItems='center'>
          <MDButton
            ref={prevRef}
            size='small'
            color='dark'
            disabled={busy || index <= 0}
            onClick={() => step(-1)}
          >
            ภาพก่อนหน้า
          </MDButton>
          <MDTypography variant='caption'>
            {index >= 0 ? `${index + 1} / ${items.length}` : '—'}
          </MDTypography>
          <MDButton
            ref={nextRef}
            size='small'
            color='dark'
            disabled={busy || index < 0 || index >= items.length - 1}
            onClick={() => step(1)}
          >
            ภาพถัดไป
          </MDButton>
        </MDBox>
        <MDBox display='flex' gap={1}>
          {/* ดาวน์โหลดภาพที่กำลังดู (A2 · 25 ต.ค. 2569) · ชื่อไฟล์ตาม fileName ของรายการ */}
          {item && (
            <MDButton
              variant='outlined'
              color='dark'
              disabled={busy || dl === 'busy'}
              onClick={() => download(item)}
              aria-label={`ดาวน์โหลดภาพ ${item.title}`}
            >
              {dl === 'busy'
                ? 'กำลังโหลด…'
                : dl === 'fail'
                ? 'โหลดไม่สำเร็จ ลองอีกครั้ง'
                : 'ดาวน์โหลด'}
            </MDButton>
          )}
          <MDButton color='dark' disabled={busy} onClick={onClose}>
            ปิด
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}
ImageReview.defaultProps = { selectedId: null, busy: false };
ImageReview.propTypes = {
  items: PropTypes.arrayOf(itemType).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
